// How the season views write numbers, records, dates and names. P6-329c (GN-2c).
//
// The design's own helpers (`D2D Hoops Seasons.dc.html`: `fmt`, `fmtDelta`, `ord`, `wl`, `rec`, `norm`, ...), ported
// once and shared by every model, so a percentage is one decimal in the hero, the splits, the leaders and the roster
// and cannot be two things on one screen (CLAUDE.md gotcha 1's copies axis).
//
// ═══════════════════════════════════════════════════════════════════════════
// EVERY DATE HERE IS DRAWN FROM AN ARRAY, NOT FROM `Intl`
// ═══════════════════════════════════════════════════════════════════════════
//
// `time.js::dayLabel` writes `Sat 3 Oct` with `Intl` in `en-GB`, and newer ICU data writes September as `Sept` in that
// locale. The seasons' games run from October to April so the difference cannot reach a game date, but the same
// helper writes a file's own date, and a label that depends on which ICU a browser ships is a label two members read
// two ways. The month and weekday names below are the design's own arrays: the same string on every engine.
//
// The ONE place `Intl` is used is the viewer's clock (`clock24`, `zoneAbbreviation`), because the zone a stamp is shown
// in is the viewer's by design (`time.js`'s header) and only the engine knows it.

/** The stat labels the views print. `−` in `+/−` is U+2212, as the design draws it. */
export const STAT = Object.freeze({
  pts: 'PTS',
  reb: 'REB',
  ast: 'AST',
  stl: 'STL',
  blk: 'BLK',
  tov: 'TOV',
  fg3m: '3PM',
  min: 'MIN',
  fg_pct: 'FG%',
  fg3_pct: '3P%',
  ft_pct: 'FT%',
  plus_minus: '+/−',
  gp: 'GP',
})

/** The stats the game-by-game chart can plot, in the order its picker lists them. */
export const PICKS = Object.freeze(['pts', 'reb', 'ast', 'fg3m', 'stl', 'blk', 'tov', 'min', 'fg_pct', 'plus_minus'])

/** The stats a player is ranked on among the players who qualified, in the order the percentile card lists them. */
export const PCT_KEYS = Object.freeze(['pts', 'reb', 'ast', 'stl', 'blk', 'fg3m', 'fg_pct', 'fg3_pct', 'ft_pct', 'tov', 'min'])

/** The categories the leaders file ranks, in the order the chips list them. */
export const LEAD_CATS = Object.freeze(['pts', 'reb', 'ast', 'stl', 'blk', 'fg3m', 'fg_pct', 'ft_pct'])

/** What each leaders category is called as a heading. */
export const CAT_NAME = Object.freeze({
  pts: 'Points',
  reb: 'Rebounds',
  ast: 'Assists',
  stl: 'Steals',
  blk: 'Blocks',
  fg3m: 'Threes made',
  fg_pct: 'Field goal %',
  ft_pct: 'Free throw %',
})

const MONTHS = Object.freeze(['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'])
const WEEKDAYS = Object.freeze(['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'])

/** A percentage key: `fg_pct`, `fg3_pct`, `ft_pct`. */
export const isPct = (key) => typeof key === 'string' && /_pct$/.test(key)

/** The sign of a number as the design writes it: `+`, `−` (U+2212) or nothing for zero. */
export const sgn = (value) => (value > 0 ? '+' : value < 0 ? '−' : '')

const isNumber = (value) => typeof value === 'number' && Number.isFinite(value)
export { isNumber }

/**
 * One stat, as text: a percentage is `×100` to one decimal (`47.6`), `plus_minus` carries its sign (`+2.9`), and
 * everything else is one decimal (`33.5`). Nothing for a value that is not a number: an empty string, never `NaN`.
 */
export function fmt(key, value) {
  if (value === null || value === undefined) return ''
  const n = Number(value)
  if (Number.isNaN(n)) return ''
  if (isPct(key)) return (n * 100).toFixed(1)
  if (key === 'plus_minus') return sgn(n) + Math.abs(n).toFixed(1)
  return n.toFixed(1)
}

/** The difference between two values of one stat: `+5.3`, a minus sign and `2.1`, or `level` when it rounds to nothing. */
export function fmtDelta(key, delta) {
  const scaled = isPct(key) ? delta * 100 : delta
  const text = Math.abs(scaled).toFixed(1)
  return text === '0.0' ? 'level' : sgn(scaled) + text
}

/** A whole number with thousands separators, in `en-US` as everywhere on this page. */
export const int = (n) => Math.round(n).toLocaleString('en-US')

/** `1st`, `2nd`, `3rd`, `4th`, `11th`, `12th`, `13th`, `21st`. */
export function ord(n) {
  const tens = n % 100
  if (tens >= 11 && tens <= 13) return `${n}th`
  return `${n}${{ 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] ?? 'th'}`
}

/** A record, with an en dash: `43` dash `21`. */
export const wl = (wins, losses) => `${wins}–${losses}`

/** The NBA's `43-21` with an en dash instead of the hyphen. */
export const rec = (text) => String(text).replace('-', '–')

/** `30-11` to `[30, 11]`. */
export const parseRec = (text) => String(text).split('-').map(Number)

/** `2025-26` to `25-26`: the short season in a comparison line. */
export const shortSeason = (season) => String(season).slice(2)

/**
 * A name with its accents taken off and its case folded, which is what a search compares: `doncic` finds
 * `Dončić`, `demin` finds `Dëmin`.
 *
 * NFD splits an accented letter into the letter and a combining mark, and the marks are dropped. A letter that has no
 * decomposition (`ø`, `đ`) is left as it is, which is the design's own `norm` and a limit worth knowing.
 */
export const fold = (text) =>
  String(text)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()

/** The last word of a name. `Thunder` from `Oklahoma City Thunder`. (See `teamNames` for the one team it is wrong for.) */
export const lastWord = (name) => String(name).trim().split(/\s+/).slice(-1)[0] ?? ''

/**
 * A team's city and nickname.
 *
 * THE BOARD'S TEAMS TABLE IS THE SOURCE WHEN IT HAS THE TEAM (`city: Portland`, `name: Trail Blazers`), because the
 * design's own rule (the last word is the nickname) is wrong for exactly one franchise and says `Portland Trail` over
 * `Blazers` for it. Without the table the standings row's full name is split by that rule, with the one exception
 * written down: a name ending in `Trail Blazers`.
 */
export function teamNames(fullName, tableEntry) {
  if (typeof tableEntry?.name === 'string' && tableEntry.name !== '') {
    return { city: String(tableEntry.city ?? '').trim(), nick: tableEntry.name }
  }
  const text = String(fullName ?? '').trim()
  const nick = /\bTrail Blazers$/.test(text) ? 'Trail Blazers' : lastWord(text)
  return { city: text.slice(0, text.length - nick.length).trim(), nick }
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/

/** A calendar date at noon UTC, so no zone can move the day, or `null` for anything that is not one. */
function dateAtNoon(isoDate) {
  const match = typeof isoDate === 'string' ? ISO_DATE.exec(isoDate) : null
  if (match === null) return null
  const when = new Date(`${isoDate}T12:00:00Z`)
  // Read it back: V8 rolls `2026-02-31` over to March rather than refusing it.
  return Number.isNaN(when.getTime()) || when.toISOString().slice(0, 10) !== isoDate ? null : when
}

/** `3 Oct`. Empty for anything that is not a date. */
export function dayMon(isoDate) {
  const when = dateAtNoon(isoDate)
  return when === null ? '' : `${when.getUTCDate()} ${MONTHS[when.getUTCMonth()]}`
}

/** `Sat 3 Oct`. */
export function wdDayMon(isoDate) {
  const when = dateAtNoon(isoDate)
  return when === null ? '' : `${WEEKDAYS[when.getUTCDay()]} ${when.getUTCDate()} ${MONTHS[when.getUTCMonth()]}`
}

/** The month's short name for a `YYYY-MM...` date: `Oct`. */
export function monthName(isoDate) {
  const month = Number(String(isoDate).slice(5, 7))
  return MONTHS[month - 1] ?? ''
}

const formatters = new Map()
function formatter(kind, timeZone, options) {
  const key = `${kind}|${timeZone ?? ''}`
  let found = formatters.get(key)
  if (!found) {
    found = new Intl.DateTimeFormat('en-GB', { ...options, ...(timeZone ? { timeZone } : {}) })
    formatters.set(key, found)
  }
  return found
}

/** `21:03`, 24-hour, in the viewer's zone (`timeZone` undefined meaning the device's). `h23`, so midnight is `00:05`. */
export function clock24(ms, timeZone) {
  return formatter('clock', timeZone, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(ms)
}

/** The viewer's calendar date at that instant, `YYYY-MM-DD` (`en-CA` writes it in that order). */
export function dateInZone(ms, timeZone) {
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  }).format(ms)
}

/** The zone's short name at that instant, as the Tonight view names it: `EDT`, `PST`, `GMT+1`. */
export function zoneAbbreviation(ms, timeZone) {
  const parts = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short', ...(timeZone ? { timeZone } : {}) }).formatToParts(ms)
  return parts.find((part) => part.type === 'timeZoneName')?.value ?? ''
}

/** Where a stamp came from, in the design's word: `NBA` for `stats.nba.com`, anything else upper-cased. */
export function historySource(source) {
  if (typeof source !== 'string' || source === '') return ''
  return source === 'stats.nba.com' ? 'NBA' : source.toUpperCase()
}
